/** @format */

export interface Tool {
  id?: string;
  name: string;
  quantity?: number;
  mpim?: string;
  value?: number;
  cost?: number;
}

export interface Part {
  id?: string;
  name: string;
  quantity: number;
  // mpim: string;
  // value: number;
}

export interface ChecklistItem {
  description: string;
  completed: boolean;
}

export interface SourceSegment {
  startTimeSeconds: number;
  endTimeSeconds: number;
  startTimeFormatted?: string;
  endTimeFormatted?: string;
}

export type Step = {
  title: string;
  // details: string[];
  details: string;
  parts: Part[];
  tools: Tool[];
  checklist: ChecklistItem[];
  imageReferences: string[]; // Array of image reference IDs
  cycleTime?: number; // Cycle time in minutes (optional, for future paid feature)
  sourceVideoId?: string;
  sourceSegment?: SourceSegment;
};

export type StatementOfOperationsOutput = {
  sopTitle: string;
  sopId: string;
  versionId: string;
  versionNum: number;
  steps: Step[];
  createdAt: string;
  titlePage?: TitlePageContent;
};

export interface TitlePageMaterial {
  name: string;
  quantity: number;
}

export interface TitlePageContent {
  details: string;
  title?: string;
  subtitle?: string;
  coverImage?: string;
  /** Materials list (e.g. from backend). Displayed on title page and in PDF. */
  materials?: TitlePageMaterial[];
  /** Revision history for title page and PDF. Derived from versions if not provided. */
  revisions?: RevisionRow[];
  authoredBy?: {
    created: {
      name: string;
      avatar: string;
      date: string;
    };
    updated: {
      name: string;
      avatar: string;
      date: string;
    };
  };
}

export class SOP {
  constructor(
    public sopId: string,
    public sopTitle: string,
    public versions: SOPVersion[],
    public createdAt: string,
    public processingStatus?: ProcessingStatusEnum,
    public processingTimeSeconds?: number,
    /** Bill of materials from API (root-level "bom"). Shown on title page and in PDF. */
    public bom?: TitlePageMaterial[],
    /** Revision history (stored in dedicated column on SOP). Displayed on title page / PDF. */
    public revisions?: RevisionRow[],
    /** Organization ID when returned by API (e.g. super admin listing). */
    public organizationId?: string,
    /** Email of the user who created the SOP (from Cognito). null if no creator. */
    public creatorEmail?: string | null,
    /** Name of the SOP's organization. null if the SOP has no org. */
    public organizationName?: string | null,
  ) {}

  getLatestVersion(): SOPVersion | undefined {
    return this.versions.reduce(
      (latest, current) => {
        return !latest || current.versionNum > latest.versionNum
          ? current
          : latest;
      },
      undefined as SOPVersion | undefined,
    );
  }

  /** Operation count for list view: from operationCount when steps omitted, else steps.length */
  getOperationCount(): number {
    const latest = this.getLatestVersion();
    if (!latest) return 0;
    if (latest.operationCount != null) return latest.operationCount;
    return latest.steps?.length ?? 0;
  }

  /**
   * Gets the best display title for this SOP with proper fallback logic
   * @returns The best available title for display
   */
  getDisplayTitle(): string {
    const latestVersion = this.getLatestVersion();
    const versionTitle = latestVersion?.versionTitle || "";

    if (versionTitle?.trim()) return versionTitle;
    if (this.sopTitle?.trim()) return this.sopTitle;
    if (!latestVersion) return "Processing SOP...";
    if (this.sopId?.trim()) return this.sopId;

    return "Untitled SOP";
  }

  /**
   * Whether the latest version is draft and thus editable.
   */
  isLatestVersionDraft(): boolean {
    const latest = this.getLatestVersion();
    return latest?.status === "draft";
  }

  /**
   * Build export payload for PDF/Markdown. When version is provided, uses that version's
   * steps, titlePage, and revisions; otherwise uses latest version and SOP-level revisions.
   */
  toStatementOfOperationsOutput(version?: SOPVersion): StatementOfOperationsOutput {
    const targetVersion = version ?? this.getLatestVersion();

    if (!targetVersion) {
      throw new Error("No version found");
    }

    const revisions: RevisionRow[] =
      targetVersion.revisions != null && targetVersion.revisions.length > 0
        ? targetVersion.revisions
        : (this.revisions && this.revisions.length > 0 ? this.revisions : []);

    const titlePage = targetVersion.titlePage
      ? {
          ...targetVersion.titlePage,
          materials: this.bom ?? targetVersion.titlePage.materials,
          revisions,
        }
      : this.bom?.length
        ? { details: "", materials: this.bom, revisions }
        : revisions.length > 0
          ? { details: "", revisions }
          : undefined;

    const sopTitle =
      version != null && version.versionTitle?.trim()
        ? version.versionTitle
        : this.getDisplayTitle();

    return {
      sopTitle,
      sopId: this.sopId,
      versionId: targetVersion.versionId,
      versionNum: targetVersion.versionNum,
      steps: targetVersion.steps,
      createdAt: this.createdAt,
      titlePage,
    };
  }
}

export type VersionStatus = "draft" | "done" | "archived";

export type SOPVersion = {
  versionId: string;
  versionNum: number;
  versionTitle: string;
  steps: Step[];
  editedAt: string;
  /** Cognito sub (UUID) of the editor. */
  editor?: string;
  /** Display name for editor (username). */
  editorName?: string;
  /** Editor's email from Cognito (single-version responses use camelCase editorEmail). Prefer in UI when present. */
  editorEmail?: string | null;
  titlePageImageRef?: string; // Reference to title page image
  titlePage?: TitlePageContent; // Full title page content
  /** Materials list (backend may return at version root; merged into titlePage for display) */
  materials?: TitlePageMaterial[];
  processingTimeSeconds?: number; // Total processing time in seconds
  /** From list endpoint when steps are omitted; use in list view instead of steps.length */
  operationCount?: number;
  /** Version status: draft (editable), done | archived (locked) */
  status?: VersionStatus;
  /** Description for this version (backend sop_description) */
  sopDescription?: string;
  /** Revision history for this version (per-version on backend) */
  revisions?: RevisionRow[] | null;
};

/** Response item from GET /sops/<sop_id>/versions (snake_case from API). */
export interface VersionListItemRaw {
  version_id: string;
  version_number: number;
  title: string;
  description?: string;
  status: VersionStatus;
  /** ISO 8601 datetime or null */
  edited_at?: string | null;
  /** Cognito sub (UUID) of the editor, or null */
  editor?: string | null;
  /** Editor's email from Cognito, or null */
  editor_email?: string | null;
  /** Allow camelCase if API sends it */
  versionId?: string;
  versionNumber?: number;
  editedAt?: string | null;
  editorEmail?: string | null;
}

/** Normalized version list item (camelCase for UI). Table shows editedAt (formatted), editorEmail or fallback to editor. */
export interface VersionListItem {
  versionId: string;
  versionNumber: number;
  title: string;
  status: VersionStatus;
  sopDescription?: string;
  editedAt?: string | null;
  editor?: string | null;
  editorEmail?: string | null;
}

export function normalizeVersionListItem(raw: VersionListItemRaw): VersionListItem {
  return {
    versionId: raw.version_id ?? raw.versionId ?? "",
    versionNumber: raw.version_number ?? raw.versionNumber ?? 0,
    title: raw.title,
    status: raw.status,
    sopDescription: raw.description ?? "-",
    editedAt: raw.edited_at ?? raw.editedAt ?? undefined,
    editor: raw.editor ?? undefined,
    editorEmail: raw.editor_email ?? raw.editorEmail ?? undefined,
  };
}

/** One row in the revision/version history table on the title page */
export type RevisionRow = {
  revision: number;
  description: string;
  created_at: string;
  created_by: string;
};

export type StepEditPayload = {
  stepIndex: number;
  newTitle: string;
  // newDetails: string[];
  newDetails: string;
  newTools?: Tool[];
  newParts?: Part[];
  newImageReferences?: string[]; // Array of image reference IDs
};

export type ProcessingStatusResponse = {
  status: ProcessingStatusEnum;
  error: string | null;
  processing_time_seconds: number | null;
};

export enum ProcessingStatusEnum {
  QUEUED = "queued",
  PROCESSING = "processing",
  PENDING_VERIFICATION = "pending_verification",
  REGENERATE = "regenerate",
  DONE = "done",
  COMPLETED = "completed",
  ERROR = "error",
}
